"use client";

import { useState } from "react";
import Image from "next/image";
import ProductPlaceholder from "./ProductPlaceholder";
import { useCart } from "../CartContext";
import { STORE_ENABLED, NUBIA_URL, SOON_LABELS, SOON_SUBLABELS } from "@/lib/store-config";

export interface PDPHeroProps {
  sku: string;
  code: string;            // "BP · 23"
  name: string;            // full
  shortName: string;
  serifTitle: React.ReactNode; // pre-styled large title
  tagline?: string;
  description: string;
  category: string;        // human label, e.g. "Age Defense · Renewal"
  volume: string;
  priceEUR: number;
  priceDisplay: string;    // already locale-formatted
  images: string[];        // ordered gallery
  reviewSummary?: { rating: number; reports: number };
  resultStats?: Array<[string, string]>; // e.g. [["+42%", "visible firmness"], ...]
  locale?: string;         // for localized showcase-mode copy
}

export default function PDPHero({
  sku,
  code,
  name,
  shortName,
  serifTitle,
  tagline,
  description,
  category,
  volume,
  priceEUR,
  priceDisplay,
  images,
  reviewSummary,
  resultStats,
  locale,
}: PDPHeroProps) {
  const [view, setView] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem, openCart } = useCart();

  const lang = locale ?? "en";
  const soonLabel = SOON_LABELS[lang] ?? SOON_LABELS.en;
  const soonSubLabel = SOON_SUBLABELS[lang] ?? SOON_SUBLABELS.en;

  // Build slides: real images first, then a bottle-illustration "schematic" card last
  const slides = images.length
    ? images.map((src, i) => ({ kind: "photo" as const, src, label: shotLabel(src), idx: i }))
    : [{ kind: "bottle" as const, label: "PRODUCT · " + code, idx: 0 }];

  const onAdd = () => {
    for (let i = 0; i < qty; i++) {
      addItem({
        sku,
        name,
        shortName,
        priceEUR,
        image: images[0],
      });
    }
    openCart();
  };

  return (
    <section
      style={{
        padding: "48px var(--bp-edge) 80px",
        borderBottom: "1px solid var(--bp-rule)",
      }}
    >
      <div className="bp-pdp-grid">
        {/* GALLERY */}
        <div className="bp-pdp-gallery">
          <div className="bp-pdp-thumbs">
            {slides.slice(0, 8).map((v, i) => (
              <button
                key={i}
                onClick={() => setView(i)}
                aria-label={`View image ${i + 1}`}
                style={{
                  aspectRatio: "1/1",
                  width: "100%",
                  border: `1px solid ${view === i ? "var(--bp-ink)" : "var(--bp-rule)"}`,
                  background: "var(--bp-bg-2)",
                  padding: 6,
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                {v.kind === "photo" ? (
                  <Image src={v.src} alt="" fill sizes="80px" style={{ objectFit: "cover" }} />
                ) : (
                  <ProductPlaceholder label={code} compact />
                )}
                <span
                  className="bp-mono-sm"
                  style={{
                    position: "absolute",
                    left: 4,
                    bottom: 4,
                    fontSize: 8,
                    color: view === i ? "var(--bp-ink)" : "var(--bp-ink-3)",
                    mixBlendMode: v.kind === "photo" ? "difference" : "normal",
                    background: v.kind === "photo" ? undefined : undefined,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              position: "relative",
              background: "var(--bp-bg-2)",
              aspectRatio: "4/5",
              overflow: "hidden",
            }}
          >
            {slides[view].kind === "photo" ? (
              <Image
                src={slides[view].src!}
                alt={name}
                fill
                priority
                sizes="(max-width:900px) 100vw, 50vw"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <ProductPlaceholder label={code} />
            )}

            <div
              style={{
                position: "absolute",
                left: 16,
                top: 16,
                fontFamily: "var(--bp-mono)",
                fontSize: 10,
                letterSpacing: "0.14em",
                color: "var(--bp-ink)",
                textTransform: "uppercase",
                mixBlendMode: "difference",
                zIndex: 4,
              }}
            >
              <div>{slides[view].label}</div>
              <div style={{ opacity: 0.6, marginTop: 2 }}>bpderma.com · {code}</div>
            </div>
            <div
              style={{
                position: "absolute",
                right: 16,
                top: 16,
                fontFamily: "var(--bp-mono)",
                fontSize: 10,
                letterSpacing: "0.14em",
                color: "var(--bp-ink)",
                textTransform: "uppercase",
                mixBlendMode: "difference",
                zIndex: 4,
              }}
            >
              FIG. {String(view + 1).padStart(2, "0")}/{String(slides.length).padStart(2, "0")}
            </div>
          </div>
        </div>

        {/* BUY BOX */}
        <div className="bp-pdp-buybox">
          <div className="bp-mono" style={{ color: "var(--bp-ink-3)" }}>
            <span className="bp-bracket">{code} · {category} · {volume}</span>
          </div>
          <h1
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              fontSize: "clamp(30px, 3.1vw, 50px)",
              lineHeight: 1.04,
              margin: "20px 0 8px",
              textWrap: "balance" as never,
              overflowWrap: "anywhere",
            }}
          >
            {serifTitle}
          </h1>
          {tagline && (
            <p
              style={{
                fontFamily: "var(--bp-serif)",
                fontStyle: "italic",
                fontWeight: 300,
                fontSize: 20,
                color: "var(--bp-ink-2)",
                margin: "0 0 24px",
                lineHeight: 1.3,
              }}
            >
              {tagline}
            </p>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              padding: "14px 0",
              borderTop: "1px solid var(--bp-rule)",
              borderBottom: "1px solid var(--bp-rule)",
              fontFamily: "var(--bp-mono)",
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--bp-ink)",
              flexWrap: "wrap",
            }}
          >
            <span>
              {reviewSummary
                ? `★★★★★ ${reviewSummary.rating.toFixed(2)} · ${reviewSummary.reports.toLocaleString("en-US")} REPORTS`
                : `★★★★★ — · NEW`}
            </span>
            <span style={{ color: "var(--bp-ink-3)" }}>SKU · {sku}</span>
          </div>

          <p
            style={{
              margin: "24px 0",
              color: "var(--bp-ink-2)",
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            {description}
          </p>

          {resultStats && resultStats.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${resultStats.length}, 1fr)`,
                border: "1px solid var(--bp-rule)",
              }}
            >
              {resultStats.map(([v, l], i) => (
                <div
                  key={i}
                  style={{
                    padding: "16px 14px",
                    borderRight: i < resultStats.length - 1 ? "1px solid var(--bp-rule)" : "none",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--bp-serif)",
                      fontSize: 30,
                      fontWeight: 300,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {v}
                  </div>
                  <div className="bp-mono-sm" style={{ marginTop: 6 }}>
                    {l}
                  </div>
                </div>
              ))}
            </div>
          )}

          {STORE_ENABLED && (
            <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "auto 1fr", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--bp-ink)" }}>
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  style={{ padding: "14px 16px", fontFamily: "var(--bp-mono)", background: "none", border: 0, cursor: "pointer" }}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span style={{ fontFamily: "var(--bp-mono)", fontSize: 12, minWidth: 24, textAlign: "center" }}>
                  {String(qty).padStart(2, "0")}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  style={{ padding: "14px 16px", fontFamily: "var(--bp-mono)", background: "none", border: 0, cursor: "pointer" }}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button onClick={onAdd} className="bp-btn" style={{ justifyContent: "center" }}>
                ADD TO BAG — {priceDisplay}
              </button>
            </div>
          )}

          {!STORE_ENABLED && (
            <div style={{ marginTop: 24 }}>
              <a
                href={NUBIA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bp-btn"
                style={{ justifyContent: "center", width: "100%", textAlign: "center" }}
              >
                {soonLabel} ↗
              </a>
              <p
                style={{
                  marginTop: 12,
                  fontFamily: "var(--bp-mono)",
                  fontSize: 11,
                  letterSpacing: "0.04em",
                  color: "var(--bp-ink-3)",
                  textTransform: "uppercase",
                }}
              >
                {soonSubLabel}
              </p>
            </div>
          )}

          {STORE_ENABLED && (
            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                ["SHIPS FROM ISTANBUL", "Free EU over €60 · 2–4 working days."],
                ["FROM THE LAB", "Free clinician chat · 15 min ↗"],
              ].map(([h, s]) => (
                <div key={h} style={{ borderTop: "1px solid var(--bp-rule)", paddingTop: 12 }}>
                  <div className="bp-mono-sm">{h}</div>
                  <div style={{ fontSize: 12, color: "var(--bp-ink-3)", marginTop: 4 }}>{s}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .bp-pdp-grid { display: grid; grid-template-columns: 1.35fr 1fr; gap: 64px; }
        .bp-pdp-gallery { display: grid; grid-template-columns: 80px 1fr; gap: 20px; }
        .bp-pdp-thumbs  { display: flex; flex-direction: column; gap: 10px; }
        .bp-pdp-buybox  { display: flex; flex-direction: column; position: sticky; top: 120px; align-self: start; min-width: 0; }
        @media (max-width: 900px) {
          .bp-pdp-grid { grid-template-columns: 1fr; gap: 40px; }
          .bp-pdp-gallery { grid-template-columns: 64px 1fr; gap: 12px; }
          .bp-pdp-buybox { position: static; }
        }
      `}</style>
    </section>
  );
}

function shotLabel(src: string): string {
  const base = src.split("/").pop() ?? "";
  if (base.includes("hero")) return "EDITORIAL";
  if (base.includes("close-up")) return "MACRO · CLOSE-UP";
  if (base.includes("application")) return "APPLICATION";
  if (base.includes("lifestyle")) return "LIFESTYLE";
  if (base.includes("with-hand")) return "WITH HAND";
  if (base.includes("texture")) return "TEXTURE";
  if (base.includes("group-shot")) return "RANGE · GROUP";
  if (base.includes("confident")) return "PORTRAIT";
  return "PRODUCT";
}
